import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { cn } from "@/utils";

export function splitVobMarkdown(markdown: string): {
  frontmatter: string;
  body: string;
} {
  const trimmed = markdown.trimStart();
  if (!trimmed.startsWith("---")) {
    return { frontmatter: "", body: markdown };
  }

  const end = trimmed.indexOf("\n---", 3);
  if (end === -1) {
    return { frontmatter: "", body: markdown };
  }

  return {
    frontmatter: trimmed.slice(4, end),
    body: trimmed.slice(end + 4).trimStart(),
  };
}

export function joinVobMarkdown(frontmatter: string, body: string): string {
  const yaml = frontmatter.trim();
  const reviewBody = body.trimStart();

  if (!yaml) return reviewBody;
  if (!reviewBody) return `---\n${yaml}\n---\n`;
  return `---\n${yaml}\n---\n\n${reviewBody}`;
}

function stripYamlFrontmatter(markdown: string): string {
  return splitVobMarkdown(markdown).body;
}

const markdownComponents: Components = {
  h1: ({ children }) => (
    <h1 className="mb-3 text-base font-semibold tracking-tight text-foreground">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="mb-2 mt-4 text-sm font-semibold text-foreground">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="mb-2 mt-3 text-sm font-medium text-foreground">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="mb-2 text-sm leading-relaxed text-foreground">{children}</p>
  ),
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  hr: () => <hr className="my-3 border-border" />,
  ul: ({ children }) => (
    <ul className="mb-2 list-disc space-y-1 pl-5 text-sm text-foreground">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-2 list-decimal space-y-1 pl-5 text-sm text-foreground">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="mb-2 border-l-2 border-border pl-3 text-sm italic text-muted-foreground">
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    const isBlock = Boolean(className);
    if (isBlock) {
      return (
        <code
          className={cn(
            "block overflow-x-auto rounded-md border bg-muted/50 p-2 font-mono text-xs",
            className,
          )}
        >
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-muted/60 px-1 py-0.5 font-mono text-xs">{children}</code>
    );
  },
  pre: ({ children }) => (
    <pre className="mb-2 overflow-x-auto rounded-md border bg-muted/50 p-2 text-xs">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="mb-3 overflow-x-auto">
      <table className="w-full min-w-[320px] border-collapse text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-border px-2 py-1.5 text-left font-semibold">{children}</th>
  ),
  td: ({ children }) => (
    <td className="border border-border px-2 py-1.5 align-top leading-relaxed">{children}</td>
  ),
};

export function VobMarkdownContent({
  content,
  className,
}: {
  content: string;
  className?: string;
}) {
  const body = stripYamlFrontmatter(content);

  return (
    <div className={cn("vob-markdown text-foreground", className)}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
        {body}
      </ReactMarkdown>
    </div>
  );
}
